import { ACTIONS, ERRORTYPES } from '../constants';
import {
  validateFirstName,
  validateLastName,
  validPhoneNumber,
  validatePhoneNumber,
  validateWebsite,
  validateCompanyName,
  trimCompanyName,
  trimWebsite,
  trimLastName
} from '../utils/profileInput';
import { validateEmail } from '../utils/formInput';

const INITIAL_STATE = {
  firstName: '',
  firstNameError: null,
  lastName: '',
  lastNameError: null,
  phoneNumber: '',
  phoneNumberError: null,
  companyName: '',
  companyNameError: null,
  website: '',
  websiteError: null,
  email: '',
  emailError: null,
  titleHoneypot: '',
  industryHoneypot: '',
  countryCode: '',
  isTransactional: 0,
  isMarketing: 0,
  isValidForm: false,
  isReseller: false,
  isAds: false,
  isUnifiedUserFlow: false
};

const validateFields = (...fields) => {
  const noError = (field) => field === null;
  return fields.every(noError);
};

const checkRequiredFields = (...fields) => {
  const isNotEmpty = (field) => {
    // Check if field has a value using Boolean() (empty string, null, and undefined evaluate to false)
    return Boolean(field);
  };
  return fields.every(isNotEmpty);
};

// Function to calculate form validity with given state
const calculateFormValidity = (state) => {
  let isValid;
  if (state.isAds) {
    isValid =
      checkRequiredFields(
        state.firstName,
        // Last name is optional only for unified users
        state.companyName,
        state.website,
        state.phoneNumber
      ) &&
      validateFields(
        state.firstNameError,
        state.lastNameError,
        state.phoneNumberError,
        state.companyNameError,
        state.websiteError
      );
  } else {
    isValid =
      checkRequiredFields(
        state.firstName,
        // Last name is optional only for unified users
        state.companyName,
        state.website,
        state.phoneNumber
      ) &&
      validateFields(
        state.firstNameError,
        state.lastNameError,
        state.phoneNumberError,
        state.companyNameError,
        state.websiteError
      );
  }
  if (state.isReseller && isValid) {
    isValid =
      checkRequiredFields(state.email) && validateFields(state.emailError);
  }
  return isValid;
};

const profileInputs = (state = INITIAL_STATE, action) => {
  // Handle the isUnifiedUserFlow flag first, before any specific action processing
  let updatedState = state;
  
  // Update isUnifiedUserFlow if it's coming from the CHECK_IS_FORM_VALID action
  if (action.type === ACTIONS.CHECK_IS_FORM_VALID && action.isUnifiedUserFlow) {
    updatedState = {
      ...state,
      isUnifiedUserFlow: true
    };
  }

  // Handle specific actions with the potentially updated state
  switch (action.type) {
    case ACTIONS.SET_FIRST_NAME: {
      const firstNameError = validateFirstName(action.firstName);
      // Create new state with updated values
      const newState = {
        ...updatedState,
        firstName: action.firstName,
        firstNameError
      };
      // Recalculate form validity with new values
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.SET_LAST_NAME: {
      // Trim the last name if it exceeds the maximum length
      const trimmedLastName = action.lastName && action.lastName.length > 0 
        ? trimLastName(action.lastName) 
        : action.lastName;
      
      const lastNameError = validateLastName(trimmedLastName, updatedState.isUnifiedUserFlow);
      
      // Create new state with updated values
      const newState = {
        ...updatedState,
        lastName: trimmedLastName,
        lastNameError
      };
      // Recalculate form validity with new values
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.SET_PHONE_NUMBER: {
      const phoneNumber = validPhoneNumber(action.phoneNumber);
      const phoneNumberError = validatePhoneNumber(phoneNumber);
      // Create new state with updated values
      const newState = {
        ...updatedState,
        phoneNumber,
        phoneNumberError
      };
      // Recalculate form validity with new values
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.SET_COMPANY_NAME: {
      const companyName = trimCompanyName(action.companyName);
      const companyNameError = validateCompanyName(companyName);
      // Create new state with updated values
      const newState = {
        ...updatedState,
        companyName,
        companyNameError
      };
      // Recalculate form validity with new values
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.SET_WEBSITE: {
      const website = trimWebsite(action.website);
      const websiteError = validateWebsite(website);
      // Create new state with updated values
      const newState = {
        ...updatedState,
        website,
        websiteError
      };
      // Recalculate form validity with new values
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.SET_EMAIL: {
      const emailErrorType = validateEmail(action.email).errorType;
      const emailError =
        emailErrorType === ERRORTYPES.NO_TYPE_SET ? null : emailErrorType;
      // Create new state with updated values
      const newState = {
        ...updatedState,
        email: action.email,
        emailError
      };
      // Recalculate form validity with new values
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.SET_TITLE_HONEYPOT: {
      return {
        ...updatedState,
        titleHoneypot: action.titleHoneypot
      };
    }
    case ACTIONS.SET_INDUSTRY_HONEYPOT: {
      return {
        ...updatedState,
        industryHoneypot: action.industryHoneypot
      };
    }
    case ACTIONS.USER_STATUS_FETCHED: {
      const newState = {
        ...updatedState,
        isReseller: action.status.is_reseller_customer
      };
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.TOGGLE_IS_TRANSACTIONAL: {
      const newState = {
        ...updatedState,
        isTransactional: 1 - updatedState.isTransactional
      };
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.TOGGLE_IS_MARKETING: {
      const newState = {
        ...updatedState,
        isMarketing: 1 - updatedState.isMarketing
      };
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.CHECK_IS_FORM_VALID: {
      // We've already handled the isUnifiedUserFlow update at the beginning of the reducer
      return {
        ...updatedState,
        isValidForm: calculateFormValidity(updatedState)
      };
    }
    case ACTIONS.SET_ADS_USER: {
      const newState = {
        ...updatedState,
        isAds: action.isAdsUser
      };
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    case ACTIONS.SET_COUNTRY_CODE: {
      const newState = {
        ...updatedState,
        countryCode: action.countryCode
      };
      return {
        ...newState,
        isValidForm: calculateFormValidity(newState)
      };
    }
    default:
      return updatedState;
  }
};

export default profileInputs;
